import { Box, Button, Center, Divider, Input, InputGroup, InputLeftElement, InputRightElement } from '@chakra-ui/react';
import { ReactComponent as GenerateIcon } from 'assets/icons/design_ai.svg';
import { ReactComponent as RightCircleSolidIcon } from 'assets/icons/right_circle_solid.svg';
import SpreadSheetImport from 'modules/common/components/SpreadSheetImport';
import {
  ChangeEvent,
  KeyboardEvent,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { SpreadSheetImportProps } from 'types/spreadsheet';

import { FileTag } from '../FileTag';
import { ChatInputProps, RightElementProps, RightElementRefProps } from './Chat.types';

/**
 * Render the chat input in the ChatPanel
 * Trigger the submit event when user press 'Enter' or click the generate button.
 *
 * If the spreadsheet upload function is needed, pass the onUpload callback function.
 *
 * @param onSubmit {function} callback function
 * @param isDisabled {boolean} optional
 * @returns
 */
export const ChatInput = ({
  containerProps,
  onSubmit,
  onUpload: onComplete,
  onClearUpload,
  isDisabled,
  disableFileUpload = false,
  placeholder,
  submitButtonRef,
  variant = 'inline',
}: ChatInputProps) => {
  const { t } = useTranslation('common_components', { useSuspense: false, keyPrefix: 'chat.promptInput' });
  const [inputValue, setInputValue] = useState('');
  const highlightButton = useMemo(() => !!inputValue.length, [inputValue]);

  const rightEleRef = useRef<RightElementRefProps>(null);

  useImperativeHandle(
    submitButtonRef,
    () => {
      if (rightEleRef.current?.submitButton) return rightEleRef.current.submitButton;

      // Should not reach here. This is the type guard
      return document.createElement('button');
    },
    [],
  );

  // Input event handler
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => setInputValue(e.target.value);
  const handleKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter' || !inputValue) return;

    handleSubmit(inputValue);
  };

  // Button event handler
  const handleClick = () => {
    if (!inputValue) return;

    handleSubmit(inputValue);
  };

  // Trigger callback event
  const handleSubmit = (inputValue: string) => {
    setInputValue('');
    onSubmit?.(inputValue);
  };

  const defaultPlaceholder = t('placeholder');
  const placeHolder = placeholder || defaultPlaceholder;
  const ChatInputWrapperTestId = 'chat-input-wrapper';
  const ChatInputTestId = 'chat-input';
  const inputAriaLabel = `Chat input: ${placeHolder}`;

  return (
    <Box pr='3' data-testid={ChatInputWrapperTestId} {...containerProps}>
      <InputGroup>
        <InputLeftElement color='#6641D6' fontSize='1.2em' children={<GenerateIcon width='24px' height='24px' />} />
        <Input
          isDisabled={isDisabled}
          boxShadow={'sm'}
          border='1px'
          placeholder={placeHolder}
          value={inputValue}
          onChange={handleChange}
          onKeyPress={handleKeyPress}
          aria-label={inputAriaLabel}
          data-testid={ChatInputTestId}
        />
        <InputRightElement width={'fit-content'} minW={'fit-content'} maxW={'max-content'} paddingX={2}>
          <RightElement
            ref={rightEleRef}
            highlightButton={highlightButton}
            isDisabled={isDisabled}
            onSubmit={variant !== 'standaloneSubmit' ? handleClick : undefined}
            onComplete={onComplete}
            onClearUpload={onClearUpload}
            disableFileUpload={disableFileUpload}
          />
        </InputRightElement>
      </InputGroup>
      {variant === 'standaloneSubmit' && (
        <Button
          ref={submitButtonRef}
          isDisabled={isDisabled}
          bgGradient='linear(to-r, violet.300, violet.600)'
          outlineOffset='0'
          _hover={{ bgGradient: 'linear(to-r, violet.600, violet.600)' }}
          _active={{
            bgGradient: 'linear(to-r, violet.600, violet.600)',
            outlineWidth: '2px',
            outlineOffset: '-2px',
            outlineColor: 'violet.800',
          }}
          color='white'
          borderRadius='base'
          maxWidth='fit-content'
          minWidth='fit-content'
          width='fit-content'
          onClick={handleClick}
          aria-label={t('generateButton')}
          data-testid='chat-input-button'
          leftIcon={<GenerateIcon width='24px' height='24px' />}
        >
          {t('generateButton')}
        </Button>
      )}
    </Box>
  );
};

export const RightElement = forwardRef<RightElementRefProps, RightElementProps>(
  (
    {
      highlightButton = false,
      isDisabled,
      disableFileUpload = false,
      onSubmit,
      onClearUpload,
      onComplete: onCompleteProp,
    },
    ref,
  ) => {
    const { t } = useTranslation('common_components', { useSuspense: false, keyPrefix: 'chat.promptInput' });
    const [fileName, setFileName] = useState('');

    const showSpreadsheetImport: boolean = !disableFileUpload && !fileName && !!onCompleteProp; // no uploads and have the upload handler
    const showFileTag = !!fileName && !!onCompleteProp; // have uploads and have the upload handler
    const showDivider = !!onSubmit && (showSpreadsheetImport || showFileTag); // have spreadsheet feature and the submit button.

    const ChatInputButtonTestId = 'chat-input-button';

    const submitRef = useRef<HTMLButtonElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const onComplete = useCallback<SpreadSheetImportProps['onComplete']>(
      (tableData, fileType, fileName) => {
        setFileName(fileName);
        onCompleteProp?.(tableData, fileType, fileName);
      },
      [onCompleteProp],
    );

    useImperativeHandle(ref, () => ({
      submitButton: submitRef.current,
      inputElement: inputRef.current,
      submitRef,
      inputRef,
    }));

    const clearUpload = useCallback(() => {
      setFileName('');
      onClearUpload?.();

      if (inputRef.current) {
        inputRef.current.value = '';
        inputRef.current.files = null;
      }
    }, [onClearUpload]);

    return (
      <>
        {showSpreadsheetImport && (
          <SpreadSheetImport ref={inputRef} isDisabled={isDisabled} icon onComplete={onComplete} />
        )}
        {showFileTag && <FileTag name={fileName} onDelete={clearUpload} />}
        {showDivider && <Divider orientation='vertical' marginX={1} height={'45%'} />}
        {!!onSubmit && (
          <Button
            ref={submitRef}
            isDisabled={isDisabled}
            variant='ghost'
            p='0'
            m='0'
            w='6'
            h='6'
            minW='6'
            borderRadius='xl'
            onClick={onSubmit}
            aria-label={t('generateButton')}
            _hover={{}} // Reset hover style
            data-testid={ChatInputButtonTestId}
          >
            <Center
              width='5'
              height='5'
              color={`${highlightButton ? 'purple.500' : 'font.300'}`}
              _hover={{ color: 'purple.500' }}
            >
              <RightCircleSolidIcon width={'100%'} height={'100%'} />
            </Center>
          </Button>
        )}
      </>
    );
  },
);

RightElement.displayName = 'RightElement';
